// src/pages/settings/ViewUser.js
import React, { useEffect, useState } from "react";
import Header from "../../components/Header";
import API_BASE_URL from "../../config";
import DataTable from "react-data-table-component";
import { Modal, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import axios from "axios";


const ViewUser = () => {
  const [users, setUsers] = useState([]);
  const [editLog, setEditLog] = useState([]);
  const [showLogModal, setShowLogModal] = useState(false);
  const [logUserId, setLogUserId] = useState(null);

  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    id: "",
    name: "",
    phone: "",
    role: "",
    email: "",
    profilePic: null,
  });
  const [editErrors, setEditErrors] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);
  const [originalPhone, setOriginalPhone] = useState("");

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState("");
  const [messageType, setMessageType] = useState(""); // success or error
  const [loadingPassword, setLoadingPassword] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  useEffect(() => {
    document.title = "View Users | Webyappy";
    fetch(`${API_BASE_URL}/auth/get_users_list`, {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data) => {
        setUsers(data);
        setLoading(false);
      });
  }, []);

  const handleEdit = (user) => {
    setEditForm({
      id: user.id,
      name: user.name,
      phone: user.phone,
      role: user.role,
      email: user.email,
      profilePic: null,
    });
    setOriginalPhone(user.phone);
    setEditErrors({});
    setShowEditModal(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm({ ...editForm, [name]: value });
  };

  const checkPhoneDuplicate = async (phone) => {
    try {
      const res = await axios.post(`${API_BASE_URL}/auth/check_duplicate`, {
        field: "phone",
        value: phone,
      });
      return res.data.exists && phone !== originalPhone;
    } catch (err) {
      console.error("Duplicate check failed:", err);
      return false;
    }
  };

  const validateEditForm = async () => {
    const errs = {};

    if (!editForm.name || editForm.name.trim() === "") {
      errs.name = "Full name is required";
    }

    if (!editForm.phone || editForm.phone.trim() === "") {
      errs.phone = "Phone is required";
    } else {
      const isDuplicate = await checkPhoneDuplicate(editForm.phone.trim());
      if (isDuplicate) {
        errs.phone = "Phone number already exists";
      }
    }

    if (!editForm.role || editForm.role.trim() === "") {
      errs.role = "Role is required";
    }

    setEditErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleUpdate = async () => {
    if (isUpdating) return;
    const isValid = await validateEditForm();
    if (!isValid) return;

    setIsUpdating(true);
    try {
      const data = new FormData();
      data.append("id", editForm.id);
      data.append("name", editForm.name);
      data.append("phone", editForm.phone);
      data.append("role", editForm.role);
      if (editForm.profilePic) {
        data.append("profilePic", editForm.profilePic);
      }

      const res = await axios.post(`${API_BASE_URL}/auth/edit_user`, data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        withCredentials: true,
      });

      console.log("Update Response:", res.data);

      if (res.data.status === "success") {
        // ✅ Show alert before closing modal
        alert("User details updated successfully.");

        const updated = await fetch(`${API_BASE_URL}/auth/get_users_list`, {
          credentials: "include",
        }).then((res) => res.json());

        setUsers(updated);
        setShowEditModal(false);
      } else {
        alert("Update failed: " + res.data.message);
      }
    } catch (err) {
      console.error("Update error", err);
      alert("Something went wrong");
    } finally {
      setIsUpdating(false);
    }
  };

  const handlePasswordChange = (user) => {
    setSelectedUserId(user.id);
    setShowPasswordModal(true);
    setOldPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setPasswordMessage("");
    setMessageType("");
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();

    if (!newPassword || !confirmPassword) {
      setMessageType("error");
      setPasswordMessage("All fields are required.");
      return;
    }

    if (newPassword.length < 8 || !/[!@#$%^&*(),.?":{}|<>]/.test(newPassword)) {
      setMessageType("error");
      setPasswordMessage(
        "New password must be at least 8 characters and include a special character.",
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessageType("error");
      setPasswordMessage("New password and confirm password do not match.");
      return;
    }

    setLoadingPassword(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/auth/admin_reset_password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            user_id: selectedUserId,
            new_password: newPassword,
          }),
        },
      );

      const data = await response.json();
      if (data.status === "success") {
        setMessageType("success");
        setPasswordMessage("Password updated successfully!");
        setNewPassword("");
        setConfirmPassword("");
      } else {
        setMessageType("error");
        setPasswordMessage(data.message || "Something went wrong");
      }
    } catch (err) {
      setMessageType("error");
      setPasswordMessage("Error connecting to server");
    } finally {
      setLoadingPassword(false);
    }
  };

  const toggleStatus = async (userId, currentStatus) => {
    const newStatus = Number(currentStatus) === 1 ? 0 : 1;

    const confirmation = window.confirm(
      `Are you sure? Do you want to change the user status to ${newStatus ? "Active" : "Inactive"}?`,
    );

    if (!confirmation) return;

    try {
      const res = await axios.post(
      `${API_BASE_URL}/auth/toggle_user_status`,
      {
        user_id: userId,
        is_active: newStatus,
      },
      {
        headers: {
          "Content-Type": "application/json", 
        },
        withCredentials: true,
      }
    );

      if (res.data.status === "success") {
        setLoading(true); // ✅ Add this to trigger UI update

        const updated = await fetch(`${API_BASE_URL}/auth/get_users_list`, {
          credentials: "include",
        }).then((res) => res.json());

        setUsers(updated);
        setLoading(false); // ✅ Reset loading after data is fetched
      } else {
        alert("Failed to update status: " + res.data.message);
      }
    } catch (err) {
      console.error("Status update error", err);
      alert("Something went wrong while changing user status.");
    }
  };

  const viewEditLog = async (userId) => {
    setLogUserId(userId);
    try {
      const res = await fetch(
        `${API_BASE_URL}/auth/get_user_edit_log?user_id=${userId}`,
      );
      const data = await res.json();

      if (data.status === "success") {
        setEditLog(data.logs);
        setShowLogModal(true);
      } else {
        alert("Failed to fetch logs.");
      }
    } catch (err) {
      console.error("Error fetching edit log:", err);
      alert("Something went wrong while fetching log.");
    }
  };

  const columns = [
    {
      name: "#",
      selector: (row, index) => index + 1,
      width: "50px",
    },
    {
      name: "Photo",
      selector: (row) => (
        <img
          src={
            row.profile_picture
              ? `${API_BASE_URL.replace("/index.php", "")}/${row.profile_picture}`
              : "/assets/media/users/default.jpg"
          }
          alt={row.name}
          className="rounded-circle"
          width={35}
          height={35}
        />
      ),
      width: "70px",
    },
    { name: "Name", selector: (row) => row.name, sortable: true },
    { name: "Phone", selector: (row) => row.phone },
    {
      name: "Email",
      selector: (row) => row.email,
      cell: (row) => (
        <span className="text-dark" style={{ whiteSpace: "nowrap" }}>
          {row.email}
        </span>
      ),
      sortable: true,
      wrap: true,
      width: "250px", // Adjust if needed
    },
    {
      name: "Role",
      cell: (row) =>
        row.role ? (
          <span className="badge badge-primary text-white">{row.role}</span>
        ) : (
          "-"
        ),
    },
    {
      name: "Status",
      cell: (row) =>
        row.is_active === 1 || row.is_active === "1" ? (
          <span className="badge badge-success">Active</span>
        ) : (
          <span className="badge badge-danger">Inactive</span>
        ),
    },
    {
      name: "Created On",
      selector: (row) => row.created_on || "-",
      sortable: true,
    },
    {
      name: "Action",
      width: "160px", // ✅ Prevent overflow
      cell: (row) => (
        <div className="d-flex align-items-center">
          <button
            type="button"
            className="btn btn-clean p-1 me-1"
            title="Edit"
            onClick={() => handleEdit(row)}
          >
            <i className="fas fa-pencil-alt text-primary"></i>
          </button>
          <button
            type="button"
            className="btn btn-clean p-1 me-1"
            title="Change Password"
            onClick={() => handlePasswordChange(row)}
          >
            <i className="fas fa-key text-warning"></i>
          </button>
          <button
            type="button"
            className="btn btn-clean p-1 me-1"
            title={row.is_active ? "Deactivate" : "Activate"}
            onClick={() => toggleStatus(row.id, row.is_active)}
          >
            <i
              className={`fas fa-power-off ${
                row.is_active ? "text-danger" : "text-success"
              }`}
            ></i>
          </button>

          <button
            type="button"
            className="btn btn-clean p-1"
            title="View Edit Log"
            onClick={() => viewEditLog(row.id)}
          >
            <i className="fas fa-clock text-dark"></i>
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <Header />
      <div style={{ paddingTop: "70px", paddingBottom: "70px" }}>
        <div className="container-fluid px-3 py-3">
          <div className="d-flex align-items-center justify-content-between mb-4">
            <ul className="breadcrumb breadcrumb-transparent breadcrumb-dot font-weight-bold mb-0 p-0">
              <li className="breadcrumb-item">
                <Link to="/home" className="text-muted">
                  Home
                </Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/pages/settings/Settings" className="text-muted">
                  Settings
                </Link>
              </li>
              <li className="breadcrumb-item">
                <span className="text-dark font-weight-bold">View Users</span>
              </li>
            </ul>
          </div>

          <div className="card card-custom shadow-sm">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-2">
                <h5 className="font-weight-bold mb-0">User List</h5>
                <Link to="/pages/settings/Settings" className="btn btn-light">
                  <i className="fas fa-arrow-left fa-xs"></i> Back
                </Link>
              </div>
              <div className="form-group mb-4">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by name, email or phone"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <DataTable
                columns={columns}
                data={users.filter(
                  (row) =>
                    row.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    row.email
                      .toLowerCase()
                      .includes(searchTerm.toLowerCase()) ||
                    row.phone.toLowerCase().includes(searchTerm.toLowerCase()),
                )}
                progressPending={loading}
                pagination
                highlightOnHover
                responsive
                striped
              />
            </div>
          </div>
        </div>
      </div>

      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form>
            <div className="form-group">
              <label className="small">
                Full Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                name="name"
                value={editForm.name}
                onChange={handleEditChange}
                placeholder="Type Here..."
              />
              {editErrors.name && (
                <small className="text-danger">{editErrors.name}</small>
              )}
            </div>

            <div className="form-group">
              <label className="small">
                Phone Number <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                name="phone"
                value={editForm.phone}
                onChange={handleEditChange}
                placeholder="Type Here..."
              />
              {editErrors.phone && (
                <small className="text-danger">{editErrors.phone}</small>
              )}
            </div>

            <div className="form-group">
              <label className="small">Email Address</label>
              <input
                type="email"
                className="form-control"
                value={editForm.email}
                disabled
              />
            </div>

            <div className="form-group">
              <label className="small">
                Role <span className="text-danger">*</span>
              </label>
              <select
                className="form-control"
                name="role"
                value={editForm.role}
                onChange={handleEditChange}
              >
                <option value="">Select Role</option>
                <option>Superadmin</option>
                <option>System User</option>
                <option>Sales</option>
              </select>
              {editErrors.role && (
                <small className="text-danger">{editErrors.role}</small>
              )}
            </div>

            <div className="form-group">
              <label className="small">Profile Picture</label>
              <input
                type="file"
                className="form-control"
                name="profilePic"
                accept="image/*"
                onChange={(e) =>
                  setEditForm({ ...editForm, profilePic: e.target.files[0] })
                }
              />
              <p className="form-text text-danger" style={{ fontSize: "11px" }}>
                JPG, JPEG, or PNG only. Max size: 2MB.
              </p>
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdate}
            disabled={isUpdating}
          >
            {isUpdating ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
                Updating...
              </>
            ) : (
              <>
                <i className="fas fa-save fa-xs mr-1"></i> Update
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showPasswordModal}
        onHide={() => setShowPasswordModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Reset Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handlePasswordReset} className="w-100">
            {/* New Password */}
            <div className="form-group mb-4 position-relative">
              <label className="custom-label">
                New Password <span className="text-danger">*</span>
              </label>
              <input
                type={showNew ? "text" : "password"}
                className="form-control custom-input pr-5"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                placeholder="Enter new password"
              />
              <i
                className={`far ${showNew ? "fa-eye-slash" : "fa-eye"} text-danger`}
                onClick={() => setShowNew(!showNew)}
                style={{
                  position: "absolute",
                  top: "38px",
                  right: "15px",
                  cursor: "pointer",
                }}
              />
            </div>

            {/* Confirm Password */}
            <div className="form-group mb-4 position-relative">
              <label className="custom-label">
                Confirm Password <span className="text-danger">*</span>
              </label>
              <input
                type={showConfirm ? "text" : "password"}
                className="form-control custom-input pr-5"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="Re-enter new password"
              />
              <i
                className={`far ${showConfirm ? "fa-eye-slash" : "fa-eye"} text-danger`}
                onClick={() => setShowConfirm(!showConfirm)}
                style={{
                  position: "absolute",
                  top: "38px",
                  right: "15px",
                  cursor: "pointer",
                }}
              />
            </div>

            {passwordMessage && (
              <div
                className={`alert alert-${messageType === "error" ? "danger" : "success"} small py-2 mb-3`}
                role="alert"
              >
                {passwordMessage}
              </div>
            )}

            <div className="text-right">
              <Button
                variant="secondary"
                onClick={() => setShowPasswordModal(false)}
              >
                Cancel
              </Button>{" "}
              <Button
                type="submit"
                variant="primary"
                disabled={loadingPassword}
              >
                {loadingPassword ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm mr-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Updating...
                  </>
                ) : (
                  <>
                    <i className="fas fa-key fa-xs mr-1"></i> Reset
                  </>
                )}
              </Button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
      <Modal
        show={showLogModal}
        onHide={() => setShowLogModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>User Edit Log</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editLog.length === 0 ? (
            <p className="text-muted">No changes logged for this user.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered">
                <thead className="thead-light">
                  <tr>
                    <th>Field</th>
                    <th>Old Value</th>
                    <th>New Value</th>
                    <th>Updated By</th>
                    <th>Updated On</th>
                  </tr>
                </thead>
                <tbody>
                  {editLog.map((log, index) => {
                    const isImage = log.field_name === "profile_picture";
                    const date = new Date(log.updated_on);
                    const formattedDate = date
                      .toLocaleString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })
                      .replace(",", "");

                    return (
                      <tr key={index}>
                        <td>{log.field_name}</td>
                        <td>
                          {isImage ? (
                            <img
                              src={`${API_BASE_URL.replace("/index.php", "")}/${log.old_value}`}
                              alt="Old"
                              width="40"
                              height="40"
                              className="rounded"
                            />
                          ) : (
                            log.old_value
                          )}
                        </td>
                        <td>
                          {isImage ? (
                            <img
                              src={`${API_BASE_URL.replace("/index.php", "")}/${log.new_value}`}
                              alt="New"
                              width="40"
                              height="40"
                              className="rounded"
                            />
                          ) : (
                            log.new_value
                          )}
                        </td>
                        <td>{log.updated_by || "-"}</td>
                        <td>{formattedDate}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ViewUser;
